import type { IframePoolShipping, ShippingChargePromo, ShippingFeesPromo } from '../api';

/** The regular figure behind each row of the fee summary, and what the
 *  promotion saves. A null figure has no regular price to strike through. */
export interface PromoSummary {
  label: string;
  discount: number;
  shipping: number | null;
  insurance: number | null;
  handling: number | null;
  total: number | null;
  /** The quote includes the shipping fee and the promotion saves something on
   *  it. Before shipping is quoted `discount` is the handling's alone, which
   *  would understate the saving; at 0 it lowers only other options. */
  showBanner: boolean;
}

/** The figures the player is shown, which the regular ones are matched to. */
export interface PlayerFigures {
  shippingFee: number | null;
  handlingFee: number | null;
  salesTax: number | null;
  total: number | null;
  /** The player's shipping fee comes split in components, so the Shipping row
   *  is carrier + signature and Insurance has its own row. */
  hasComponents: boolean;
}

const round2 = (value: number) => Math.round(value * 100) / 100;

/**
 * Lays a quote's regular figures out the way the player's own rows are laid
 * out (same split, same total basis), so every row can strike its regular
 * figure through next to the one the player pays. The sales tax is not
 * discounted, so the regular total carries the tax the player is charged.
 * Null without a promotion.
 */
export function buildPromoSummary(
  promo: ShippingFeesPromo | null | undefined,
  player: PlayerFigures,
): PromoSummary | null {
  if (!promo) return null;

  const split = promo.originalShippingFeeComponents;
  const shipping = player.hasComponents
    ? split
      ? round2(split.base + split.signature)
      : null
    : promo.originalShippingFee;

  // Mirror the player's total: only the fees it already counts, and unknown
  // when a regular figure is missing for one of them.
  const shippingPart = player.shippingFee === null ? 0 : promo.originalShippingFee;
  const handlingPart = player.handlingFee === null ? 0 : promo.originalHandlingFee;
  const total =
    player.total === null || shippingPart === null || handlingPart === null
      ? null
      : round2(shippingPart + handlingPart + (player.salesTax ?? 0));

  return {
    label: promo.label,
    discount: promo.discount,
    shipping,
    insurance: split ? split.insurance : null,
    handling: promo.originalHandlingFee,
    total,
    showBanner: player.shippingFee !== null && promo.discount > 0,
  };
}

/** A delivery that was canceled, or whose cards were all refunded, kept none of
 *  what a promotion saved on it. */
export function isRefundedShipment(shipping: Pick<IframePoolShipping, 'status' | 'settleSummary'>): boolean {
  return shipping.status === 'canceled' || shipping.settleSummary === 'refunded';
}

/** Regular shipping + handling of a delivery that was already charged; null
 *  when either regular fee is unknown. */
export function regularFees(promo: ShippingChargePromo): number | null {
  if (promo.originalShipping === null || promo.originalHandling === null) return null;
  return round2(promo.originalShipping + promo.originalHandling);
}
